import type { WorkbookApi } from '@mathflat/domain/@entities/(Content)/Workbook/api'
import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'

import { workbookApi } from '@/api/workbook.api'
import { WorkbookPage, WorkbookPageColl, WorkbookPageProblem, WorkbookPageProblemColl } from '@/dto'
import { FlatTree } from '@/models/FlatTree'
import type { RawMakeProblemT } from '@/page/MakeCustomWorkbook/context/abstract/baseMakeCustomWorkbookContext'
import type { RawMakeChapterT } from '@/page/MakeCustomWorkbook/context/abstract/baseMakeCustomWorkbookContext'
import type { CustomRequestConfigMeta } from '@/utils/axios/maxios'

import { makeAdapter } from './Adapter'

export class WorkbookController {
  adapter = {
    workbook: {
      fromSupportedWorkbookPageList({
        pageProblems,
        ...response
      }: WorkbookApi.GetSupportedWorkbookPageList.Response) {
        return new WorkbookPage({
          ...response,
          pageProblemColl: new WorkbookPageProblemColl(
            pageProblems.map((p) => new WorkbookPageProblem(p)),
          ),
        })
      },
    },
    inspectWorkbookList: makeAdapter({
      from(data: res.InspectWorkbookList) {
        return data.map(({ bookId }) => {
          return Number(bookId)
        })
      },
    }),
    customWorkbookRequest: {
      /**
       * Custom Workbook 만들기 트리에 보관된 응답 객체를 POST 요청 스펙 형태로 변환.
       * `chapters[].children[]` 노드에서 요청에 포함되지 않는 필드(`sourceType` 등)를 제거.
       */
      to<
        T extends WorkbookApi.CustomWorkbookType,
        U extends T extends 'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
          ? 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER'
          : never = never,
      >(
        payload: WorkbookApi.PostPutCustomWorkbook.Request<T, U>,
      ): WorkbookApi.PostPutCustomWorkbook.Request<T, U> {
        const convertNode = (node: unknown): unknown => {
          if (!node || typeof node !== 'object') return node
          const { sourceType, ...rest } = node as Record<string, unknown> & { sourceType?: unknown }
          void sourceType
          const children = (rest as { children?: unknown }).children
          if (Array.isArray(children)) {
            return { ...rest, children: children.map(convertNode) }
          }
          return rest
        }
        const chapters = payload.chapters as unknown
        if (!Array.isArray(chapters)) return payload
        return {
          ...payload,
          chapters: chapters.map(convertNode),
        } as WorkbookApi.PostPutCustomWorkbook.Request<T, U>
      },
    },
    chapterProblems: makeAdapter({
      from(data: WorkbookApi.GetProblems.Response) {
        const processChildren = (
          item: WorkbookApi.GetProblems.Child,
        ): RawMakeProblemT | RawMakeChapterT => {
          if (!('children' in item) && item.type === 'PROBLEM') {
            return FlatTree.treeNode.fromRawDataToRawTreeNode(item, 'id')
          }

          if (item.children) {
            return FlatTree.treeNode.fromRawDataToRawTreeNode({
              ...item,
              children: item.children.map(processChildren),
            }) as RawMakeChapterT
          }

          throw new Error('Invalid item')
        }
        return data.chapters.map((c) => processChildren(c))
      },
    }),
  }

  customWorkbook = {
    getProblemCount: async <T extends WorkbookApi.CustomWorkbookType>(
      payload: WorkbookApi.GetProblemCount.Request<T>,
      meta?: CustomRequestConfigMeta,
    ) => {
      return workbookApi.customWorkbook.getProblemCount<T>(payload, meta)
    },
    getChapterProblemsByFilter: async <
      T extends WorkbookApi.CustomWorkbookType = WorkbookApi.CustomWorkbookType,
      U extends T extends 'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
        ? 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER'
        : never = never,
    >(
      payload: WorkbookApi.GetProblems.Request<T>,
      meta?: CustomRequestConfigMeta,
    ) => {
      const res = await workbookApi.customWorkbook.getChapterProblemsByFilter<T, U>(payload, meta)

      return this.adapter.chapterProblems.from(res)
    },
    getChapterProblemsByWorkbookId: async <
      T extends WorkbookApi.CustomWorkbookType = WorkbookApi.CustomWorkbookType,
      U extends T extends 'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
        ? 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER'
        : never = never,
    >(
      workbookId: WorkbookApi.GetCustomWorkbookById.Request['workbookId'],
      meta?: CustomRequestConfigMeta,
    ) => {
      const res = await workbookApi.customWorkbook.getChapterProblemsByWorkbookId<T, U>(
        workbookId,
        meta,
      )

      return this.adapter.chapterProblems.from(res)
    },
    getWorkbookById: async (
      workbookId: WorkbookApi.GetCustomWorkbookById.Request['workbookId'],
      meta?: CustomRequestConfigMeta,
    ) => {
      const { data } = await workbookApi.customWorkbook.getWorkbookById(workbookId, {
        useAbortController: true,
        ...meta,
      })
      return data
    },
    postWorkbook: async <
      T extends WorkbookApi.CustomWorkbookType,
      U extends T extends 'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
        ? 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER' // CHAPTER일 경우 소단원인지 중단원인지
        : never = never,
    >(
      payload: WorkbookApi.PostPutCustomWorkbook.Request<T, U>,
      meta?: CustomRequestConfigMeta,
    ) => {
      const { data } = await workbookApi.customWorkbook.postWorkbook<T, U>(
        this.adapter.customWorkbookRequest.to<T, U>(payload),
        meta,
      )
      return data.workbookId
    },
    putWorkbook: async <
      T extends WorkbookApi.CustomWorkbookType,
      U extends T extends 'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
        ? 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER' // CHAPTER일 경우 소단원인지 중단원인지
        : never = never,
    >(
      workbookId: WorkbookApi.GetCustomWorkbookById.Request['workbookId'],
      payload: WorkbookApi.PostPutCustomWorkbook.Request<T, U>,
      meta?: CustomRequestConfigMeta,
    ) => {
      const { data } = await workbookApi.customWorkbook.putWorkbook<T, U>(
        workbookId,
        this.adapter.customWorkbookRequest.to<T, U>(payload),
        meta,
      )
      return data.workbookId
    },
    getWorkbookProblemById: async <T extends WorkbookApi.CustomWorkbookType>(
      workbookId: WorkbookApi.GetCustomWorkbookProblemById.Request['workbookId'],
      meta?: CustomRequestConfigMeta,
    ) => {
      const { data } = await workbookApi.customWorkbook.getWorkbookProblemById<T>(workbookId, meta)
      return data
    },
    getInnerDesignTemplate: async <T extends WorkbookApi.CustomWorkbookType>(
      payload: WorkbookApi.PostInnerDesignTemplate.Request<T>,
      meta?: CustomRequestConfigMeta,
    ) => {
      const { data } = await workbookApi.customWorkbook.getInnerDesignTemplate<T>(payload, meta)
      return data
    },

    deleteWorkbooks: async (workbookIds: WorkbookEntity.CustomWorkbook['id'][]) =>
      await workbookApi.customWorkbook.deleteWorkbooks(workbookIds),
  }

  supportedWorkbook = {
    fetchWorkbookProblemPageList: async (workbookId: WorkbookEntity.Workbook['id']) => {
      const { data } = await workbookApi.supportedWorkbook.getSupportedWorkbookPageList(workbookId)

      const workbookPageColl = new WorkbookPageColl(
        data.map(this.adapter.workbook.fromSupportedWorkbookPageList),
      )

      return workbookPageColl
    },
  }

  fetchAssignedStudentList = async (workbookId: WorkbookEntity.Workbook['id']) => {
    const { data } = await workbookApi.getAssignedStudentList(workbookId)
    return data
  }

  customSignatureWorkbook = {
    deleteSignatureWorkbook: async (workbookId: WorkbookEntity.CustomSignatureWorkbook['id']) => {
      await workbookApi.customSignatureWorkbook.deleteSignatureWorkbook(workbookId)
    },
    restoreWorkbookCustomSignature: async (id: WorkbookEntity.CustomSignatureWorkbook['id']) => {
      return workbookApi.customSignatureWorkbook.restoreCustomSignatureWorkbook(id)
    },
    postFilter: async (payload: WorkbookApi.CreateCustomSignatureWorkbookFilter.Request) => {
      const {
        data: { filterId },
      } = await workbookApi.customSignatureWorkbook.postFilter(payload)
      return filterId
    },
    getFilterByFilterId: async (
      filterId: WorkbookApi.CreateUpdateSignatureWorkbookFilter.Response['filterId'],
    ) => {
      const { data } = await workbookApi.customSignatureWorkbook.getFilterByFilterId(filterId)
      return data
    },
    postCustomSignature: async (
      payload: WorkbookApi.CreateUpdateCustomSignatureWorkbook.Request,
    ) => {
      const res = await workbookApi.customSignatureWorkbook.postCustomSignature(payload)
      return res.data
    },
    putCustomSignature: async (
      id: WorkbookEntity.CustomSignatureWorkbook['id'],
      payload: WorkbookApi.CreateUpdateCustomSignatureWorkbook.Request,
    ) => {
      const { data } = await workbookApi.customSignatureWorkbook.putCustomSignature(id, payload)
      return data
    },
  }

  fetchWorkbookDownload = async (params: Omit<req.WorkbookDownloadParamsV2, 'version'>) => {
    const res = await workbookApi.getWorkbookDownload(params)
    return res.data
  }
  fetchInspectWorkbookList = async () => {
    const { data } = await workbookApi.getInspectWorkbooks()
    return this.adapter.inspectWorkbookList.from(data)
  }
}

export const workbookController = new WorkbookController()
